import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { Shield, RefreshCw, Trash2, X, Info } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABEL = {
  "auth.login.success": { label: "Login Berhasil", cls: "bg-emerald-100 text-emerald-800" },
  "auth.login.failed": { label: "Login Gagal", cls: "bg-rose-100 text-rose-800" },
  "auth.logout": { label: "Logout", cls: "bg-slate-100 text-slate-700" },
};

function ActionBadge({ action }) {
  const meta = ACTION_LABEL[action] || { label: action, cls: "bg-indigo-100 text-indigo-800" };
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${meta.cls}`}>{meta.label}</span>;
}

function formatDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
}

export default function AuditLog() {
  const [filters, setFilters] = useState({ q: "", role: "", action: "", ip: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const LIMIT = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: LIMIT, ...applied };
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] === undefined) && delete params[k]);
      const { data } = await api.get("/admin/audit-log", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [applied, skip]);
  useEffect(() => { load(); }, [load]);

  const apply = () => { setSkip(0); setApplied(filters); };
  const reset = () => { setFilters({ q: "", role: "", action: "", ip: "", date_from: "", date_to: "" }); setApplied({}); setSkip(0); };

  const openSummary = async (userId) => {
    try {
      const { data } = await api.get("/admin/audit-log/summary", { params: { user_id: userId } });
      setSummary(data.contexts);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus entri log ini?")) return;
    try { await api.delete(`/admin/audit-log/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Log Aktivitas (Audit)" action={
        <button onClick={load} className="pp-btn-secondary" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      } />

      <section className="pp-card p-5 mb-4">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <input className="pp-input" placeholder="Cari nama/user/endpoint…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="audit-q" />
          <select className="pp-input" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="audit-role">
            <option value="">Semua Role</option>
            <option value="admin">Admin</option>
            <option value="kepala_sekolah">Kepala Sekolah</option>
            <option value="bendahara">Bendahara</option>
            <option value="guru">Guru</option>
            <option value="orangtua">Orang Tua</option>
            <option value="pustaka">Pustaka</option>
          </select>
          <input className="pp-input" placeholder="Filter action (kendala.create, auth.login)" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-action" />
          <input className="pp-input" placeholder="Filter IP" value={filters.ip} onChange={(e) => setFilters({ ...filters, ip: e.target.value })} data-testid="audit-ip" />
          <input type="date" className="pp-input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="audit-from" />
          <input type="date" className="pp-input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="audit-to" />
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={reset} className="pp-btn-secondary">Reset</button>
          <button onClick={apply} className="pp-btn-primary" data-testid="audit-apply">Terapkan</button>
        </div>
      </section>

      <section className="pp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-xs" data-testid="audit-table">
            <thead><tr className="pp-table-header text-xs">
              <th className="text-left px-4 py-3">Tanggal & Jam</th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Aksi</th>
              <th className="text-left px-4 py-3">Endpoint</th>
              <th className="text-left px-4 py-3">IP</th>
              <th className="text-left px-4 py-3">Device</th>
              <th className="text-right px-4 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Memuat…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada log yang cocok.</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className="pp-row" data-testid={`audit-row-${it.id}`}>
                  <td className="px-4 py-3 font-mono text-[11px]">{formatDT(it.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.nama || it.username || "—"}</div>
                    <div className="text-[10px] text-slate-500">{it.role || "—"}</div>
                  </td>
                  <td className="px-4 py-3"><ActionBadge action={it.action} /></td>
                  <td className="px-4 py-3 font-mono text-[10px] text-slate-600">{it.method} {it.endpoint}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{it.ip}</td>
                  <td className="px-4 py-3 text-[11px]">
                    <div>{it.ua_browser} · {it.ua_device}</div>
                    <div className="text-[10px] text-slate-500">{it.ua_os}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setDetail(it)} className="pp-btn-ghost" title="Detail"><Info className="w-3 h-3" /></button>
                      {it.user_id && (
                        <button onClick={() => openSummary(it.user_id)} className="pp-btn-ghost" title="Ringkasan device user ini"><Shield className="w-3 h-3" /></button>
                      )}
                      <button onClick={() => del(it.id)} className="pp-btn-danger"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-600 border-t border-slate-100">
            <button disabled={skip <= 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} className="pp-btn-secondary disabled:opacity-40">← Sebelumnya</button>
            <span>Menampilkan {skip + 1}–{Math.min(skip + LIMIT, total)} dari {total}</span>
            <button disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)} className="pp-btn-secondary disabled:opacity-40">Berikutnya →</button>
          </div>
        )}
      </section>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h4 className="font-semibold">Detail Log</h4>
              <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <pre className="p-5 text-xs font-mono whitespace-pre-wrap bg-slate-50">{JSON.stringify(detail, null, 2)}</pre>
          </div>
        </div>
      )}
      {summary && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h4 className="font-semibold flex items-center gap-2"><Shield className="w-4 h-4" /> Ringkasan Perangkat User</h4>
              <button onClick={() => setSummary(null)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {summary.length === 0 ? (
                <div className="text-slate-400 italic text-sm text-center py-8">Belum ada aktivitas sukses.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead><tr className="pp-table-header">
                    <th className="text-left px-3 py-2">IP</th>
                    <th className="text-left px-3 py-2">Browser</th>
                    <th className="text-left px-3 py-2">OS</th>
                    <th className="text-left px-3 py-2">Device</th>
                    <th className="text-left px-3 py-2">Aktivitas</th>
                    <th className="text-left px-3 py-2">Terakhir</th>
                  </tr></thead>
                  <tbody>
                    {summary.map((c, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-mono">{c.ip}</td>
                        <td className="px-3 py-2">{c.browser}</td>
                        <td className="px-3 py-2">{c.os}</td>
                        <td className="px-3 py-2">{c.device}</td>
                        <td className="px-3 py-2 text-center">{c.count}</td>
                        <td className="px-3 py-2 font-mono text-[10px]">{formatDT(c.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
